import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  SlidersHorizontal,
  MoreHorizontal,
  ThumbsUp,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  CheckCheckIcon,
  Undo,
  CheckCheck,
} from 'lucide-react';

import { Preview, Remove, Suspend, Dove, comment } from '../../assets/images';

import ActionModal from '../components/Modals/ActionModal';
import api from '../../../services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capitalize, getAssetUrl, getTimeAgo } from '../../../services/helper';

const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");

// ─────────────────────────────────────────────────────────────────────────────
// Avatar Components + Helpers (unchanged but cleaned)
// ─────────────────────────────────────────────────────────────────────────────

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Avatar({ name, color, avatar }) {
  const hexColor = AVATAR_HEX[color] || '#6366F1';
  return (
    <div
      className={`w-12 h-12 sm:w-[54px] sm:h-[54px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center ${color}`}
      style={{ backgroundColor: hexColor }}
    >
      {avatar ? (
        <img
          src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
          alt={name}
          className="w-full h-full object-cover rounded-full"
        />
      ) : (
        <span className="text-white font-bold text-lg">{getInitials(name)}</span>
      )}
    </div>
  );
}
function AvatarSmall({ name, avatar }) {
  console.log({ name, avatar });
  return (
    <div
      className={`w-8 h-8 sm:w-[34px] sm:h-[34px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center`}

    >
      {/* {avatar ? ( */}
      <img
        src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
        alt={name}
        className="w-full h-full object-cover rounded-full"
      />
      {/* ) : (
        <span className="text-white font-bold text-lg">{getInitials(name)}</span>
      )} */}
    </div>
  );
}
const PostStatusBadge = ({ status }) => {
  // if (!status) return null;

  const config = {
    pending: {
      label: "flagged",
      color: "bg-amber-100 text-amber-700 border-amber-200",
      icon: "⏳", // or import Clock from lucide-react
    },
    suspended: {
      label: "Suspended",
      color: "bg-red-100 text-red-700 border-red-200",
      icon: "🚫",
    },
    approved: {
      label: "Approved",
      color: "bg-emerald-100 text-emerald-700 border-emerald-200",
      icon: "✅",
    },
    removed: {
      label: "Removed",
      color: "bg-gray-100 text-gray-700 border-gray-200",
      icon: "🗑️",
    },
  };

  const current = config[status] || {
    label: "Approved",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: "✅",
  };

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${current.color} whitespace-nowrap self-start`}>
      <span className="text-base">{current.icon}</span>
      <span>{current.label}</span>
    </div>
  );
};

function CircleAvatar({ name, hexColor, size = 40 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: hexColor || '#6366F1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: Math.floor(size * 0.34),
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

// AVATAR_HEX (kept from previous version)
const AVATAR_HEX = {
  'bg-rose-400': '#FB7185',
  'bg-amber-500': '#F59E0B',
  'bg-teal-500': '#14B8A6',
  'bg-indigo-400': '#818CF8',
  'bg-pink-400': '#F472B6',
  'bg-sky-400': '#38BDF8',
  'bg-green-500': '#22C55E',
  'bg-purple-500': '#A855F7',
  'bg-emerald-500': '#10B981',
};

// ─────────────────────────────────────────────────────────────────────────────
// CommentItem & PreviewModal (same as before - minor improvements)
// ─────────────────────────────────────────────────────────────────────────────

function CommentItem({ comment }) {
  console.log({ comment_item: comment });
  const [showReplies, setShowReplies] = useState(false);


  return (
    <div className="mb-5">
      <div className="flex gap-3">
        <AvatarSmall name={comment?.author?.name} avatar={comment?.author?.avatar} />

        {/* <CircleAvatar name={comment.name} hexColor={comment.avatarColor} size={40} /> */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-sm">{comment?.author?.name}</span>
            <span className="text-xs text-gray-400">{getTimeAgo(comment?.createdAt)}</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed mb-3">{comment?.content}</p>
          {/* <div className="flex justify-end gap-6 text-sm text-gray-500">
            <button className="flex items-center gap-1 hover:text-gray-700 transition-colors">
              <ThumbsUp size={14} />
              <span>{comment?.bless_count}</span>
            </button>
            {comment?.comment_count > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center gap-1 hover:text-gray-700 transition-colors"
              >
                <MessageCircle size={14} />
                <span>{comment.comment_count}</span>
              </button>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
}
function CommentPreviewModal({ item, onClose }) {
  const authorName = item?.author?.name || item?.name;
  const authorAvatar = item?.author?.avatar || item?.avatar;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[24px] w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl hide-scrollbar"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        <div className="px-6 pt-6 flex items-center gap-3">
          <AvatarSmall name={authorName} avatar={authorAvatar} />

          <div>
            <span className="font-bold text-[16px] text-black">{authorName}</span>
            <div className="text-[13px] text-gray-400 mt-1">
              {getTimeAgo(item?.createdAt || item?.created_at)}
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-[15px] leading-[1.8] text-gray-700 whitespace-pre-wrap">
            {item?.content}
          </p>
        </div>
      </div>
    </div>
  );
}
function PreviewModal({ item, onClose }) {
  console.log({item});
  if (item.content_type === "comment") {
    return <CommentPreviewModal item={item} onClose={onClose} />;
  }

  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [isBlessed, setIsBlessed] = useState(false);

  const hexColor = AVATAR_HEX[item?.avatarColor] || "#6366F1";

  const {
    data: commentsResponse,
    isLoading: commentsLoading,
    refetch: loadComments,
  } = useQuery({
    queryKey: ["post-comments", item?.id],
    queryFn: () => api.getComments(item.id),
    enabled: false,
  });

  const comments = commentsResponse?.comments || [];

  console.log({ comments });
  if (!item) return null;
  const isImage = (p) => /\.(jpe?g|png|gif|webp|jfif|avif|svg)$/i.test(p);

  const handleLoadComments = async () => {
    setShowComments((prev) => !prev);

    if (!showComments && !comments.length) {
      await loadComments();
    }
  };

  const handlePost = () => {
    if (!commentText.trim()) return;

    // Optional local update
    setCommentText("");
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[24px] w-full max-w-[720px] max-h-[90vh] overflow-y-auto shadow-2xl hide-scrollbar"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>
          {`
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        {/* Header */}
        <div className="px-6 pt-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar
              name={item.name}
              avatar={item?.avatar}
              hexColor={hexColor}
              size={48}
            />

            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-[16px] text-black">
                  {item.name}
                </span>
              </div>

              <div className="text-[13px] text-gray-400 mt-1">
                {getTimeAgo(item.created_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {item.post_type == "normal" && <>        <div className="px-6 pt-5">
          <p className="text-[15px] leading-[1.8] text-gray-700">
            {item?.content}
          </p>
        </div>

          {/* Media */}
          <div className="relative mx-6 mt-5 rounded-[16px] overflow-hidden">
            {item?.attachment && (
              <div className="relative">
                {/\.(jpe?g|png|gif|webp|avif|svg)$/i.test(item?.attachment) ? (
                  <img
                    src={
                      item?.attachment?.startsWith("/uploads")
                        ? `${API_ROOT}${item.attachment}`
                        : item.attachment
                    }
                    alt=""
                    className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
                  />
                ) : (
                  <video
                    src={
                      item?.attachment?.startsWith("/uploads")
                        ? `${API_ROOT}${item.attachment}`
                        : item.attachment
                    }
                    controls
                    className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
                  />
                )}
              </div>
            )}
          </div>
        </>}
        {item.post_type == "testimony" && <>     <div className="px-6 pt-5 grid grid-cols-2 gap-4 sm:gap-6">
          {/* Before */}
          <div>
            <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">Before</p>
            <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
              {item.before_text}
            </p>
            {item.before_media && (
              <div className="mt-3 rounded-xl overflow-hidden">
                {isImage(item.before_media) ? (
                  <img src={getAssetUrl(item.before_media)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
                ) : (
                  <video src={getAssetUrl(item.before_media)} controls className="w-full max-h-[200px] rounded-xl" />
                )}
              </div>
            )}
          </div>

          {/* After */}
          <div>
            <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">After</p>
            <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
              {item.after_text || ""}
            </p>
            {item.after_media && (
              <div className="mt-3 rounded-xl overflow-hidden">
                {isImage(item.after_media) ? (
                  <img src={getAssetUrl(item.after_media)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
                ) : (
                  <video src={getAssetUrl(item.after_media)} controls className="w-full max-h-[200px] rounded-xl" />
                )}
              </div>
            )}
          </div>
        </div></>}

        {/* Actions */}
        <div className="px-6 py-5 flex items-center gap-6">
          <button className="flex items-center gap-2.5 transition-all">
            <img
              src={Dove}
              alt="Bless"
              className={`w-[30px] h-[30px] object-contain transition-all duration-300 ${isBlessed ? "grayscale-0" : "grayscale"
                }`}
            />
            <span className="text-[14px] font-medium text-gray-500">
              {item?.bless_count} Bless
            </span>
          </button>

          <button
            onClick={handleLoadComments}
            className="flex items-center gap-2.5 transition-all"
          >
            <img
              src={comment}
              alt="Comments"
              className="w-[30px] h-[30px] object-contain"
            />

            <span
              className={`text-[14px] font-medium ${showComments ? "text-blue-600" : "text-gray-500"
                }`}
            >
              {item?.comment_count} Comments
            </span>
          </button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="px-6 pb-6">


            {commentsLoading ? (
              <div className="text-center py-5 text-gray-500">
                Loading comments...
              </div>
            ) : comments.length > 0 ? (
              comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                />
              ))
            ) : (
              <div className="text-center py-5 text-gray-500">
                No comments found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
// function PreviewModal({ item, onClose }) {
//   const [showComments, setShowComments] = useState(false);
//   const [commentText, setCommentText] = useState('');
//   const [comments, setComments] = useState([]);

//   if (!item) return null;

//   const handlePostComment = () => {
//     if (!commentText.trim()) return;
//     setComments((prev) => [
//       {
//         id: Date.now(),
//         name: 'You',
//         created_at: new Date().toISOString(),
//         content: commentText,
//         bless_count: 0,
//         comment_count: 0,
//         avatarColor: '#6366F1',
//       },
//       ...prev,
//     ]);
//     setCommentText('');
//     setShowComments(true);
//   };

//   return (
//     <div onClick={onClose} className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4">
//       <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
//         {/* Header */}
//         <div className="px-6 pt-6 pb-4 border-b flex items-start justify-between">
//           <div className="flex items-center gap-3">
//             <Avatar name={item.name} color={item.avatarColor} avatar={item.avatar} />
//             <div>
//               <div className="font-bold text-lg">{item.name}</div>
//               <div className="text-sm text-gray-500">
//                 {getTimeAgo(item.created_at)} • {capitalize(item.content_type)}
//               </div>
//             </div>
//           </div>
//           <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
//             <MoreHorizontal size={20} />
//           </button>
//         </div>

//         <div className="flex-1 overflow-auto p-6 space-y-6">
//           <p className="text-[15px] leading-relaxed text-gray-700">{item.content}</p>

//           {item.attachment && (
//             <div className="rounded-2xl overflow-hidden border border-gray-100">
//               {/\.(jpe?g|png|gif|webp|avif)$/i.test(item.attachment) ? (
//                 <img src={item.attachment.startsWith("/uploads") ? `${API_ROOT}${item.attachment}` : item.attachment} alt="attachment" className="w-full max-h-[420px] object-cover" />
//               ) : (
//                 <video src={item.attachment.startsWith("/uploads") ? `${API_ROOT}${item.attachment}` : item.attachment} controls className="w-full" />
//               )}
//             </div>
//           )}

//           <div className="flex items-center gap-8 py-2">
//             <button className="flex items-center gap-2.5">
//               <img src={Dove} alt="Bless" className="w-8 h-8" />
//               <span className="font-medium text-gray-600">{item.bless_count} Bless</span>
//             </button>
//             <button onClick={() => setShowComments(v => !v)} className="flex items-center gap-2.5">
//               <img src={comment} alt="Comments" className="w-8 h-8" />
//               <span className={`font-medium ${showComments ? 'text-blue-600' : 'text-gray-600'}`}>
//                 {item.comment_count} Comments
//               </span>
//             </button>
//           </div>

//           {showComments && (
//             <div className="pt-4 border-t">
//               <div className="flex gap-3 mb-8">
//                 <CircleAvatar name="You" hexColor="#6366F1" size={42} />
//                 <div className="flex-1 flex bg-gray-50 border border-gray-200 rounded-full px-5 py-1">
//                   <input
//                     type="text"
//                     value={commentText}
//                     onChange={(e) => setCommentText(e.target.value)}
//                     onKeyDown={(e) => e.key === 'Enter' && handlePostComment()}
//                     placeholder="Write a comment..."
//                     className="flex-1 bg-transparent outline-none text-sm"
//                   />
//                   <button onClick={handlePostComment} className="ml-2 px-6 py-2 bg-[#1E2D4F] text-white text-sm font-semibold rounded-full hover:bg-[#15213d]">
//                     Post
//                   </button>
//                 </div>
//               </div>
//               {comments.map((c) => <CommentItem key={c.id} comment={c} />)}
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// ─────────────────────────────────────────────────────────────────────────────
// Pagination Component
// ─────────────────────────────────────────────────────────────────────────────

function Pagination({ pagination, onPageChange }) {
  const { page, totalPages, hasNextPage, hasPrevPage } = pagination;

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-3 mt-10">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={!hasPrevPage}
        className="flex items-center gap-1 px-4 py-2 rounded-xl border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        <ChevronLeft size={18} />
        Previous
      </button>

      <span className="text-sm text-gray-600 font-medium">
        Page {page} of {totalPages}
      </span>

      <button
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNextPage}
        className="flex items-center gap-1 px-4 py-2 rounded-xl border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ActionButton & PostCard (same as before)
// ─────────────────────────────────────────────────────────────────────────────

function ActionButton({ icon, label, danger = false, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full sm:w-[150px] h-[50px] rounded-[10px] border
        flex items-center justify-center gap-2.5 text-[15px] font-semibold
        transition-all duration-200
        ${danger
          ? 'border-red-200 text-red-500 hover:bg-red-50'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }
      `}
    >
      {children || (icon && <img src={icon} alt={label} className="w-[22px] h-[22px] object-contain" />)}
      {label}
    </button>
  );
}
function ActionButtonv2({ icon, label, danger = false, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full sm:w-[150px] h-[50px] rounded-[10px] border
        flex items-center justify-center gap-2.5 text-[15px] font-semibold
        transition-all duration-200
        ${danger
          ? 'border-red-200 text-red-500 hover:bg-red-50'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}
function PostCard({ item, onPreview, onRemove, onSuspend, onApprove }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
      <div className="p-6">
        <div className="flex gap-4 mb-5">
          <Avatar name={item.name} color={item.avatarColor} avatar={item.avatar} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-xl text-gray-900 truncate">
                {item.name}
              </span>
              <span className="text-xs px-3 py-1 bg-gray-100 text-gray-600 rounded-full font-medium">
                {capitalize(item.content_type)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {getTimeAgo(item.created_at)}
            </p>
          </div>
        </div>

        {/* <p className="text-[15px] text-gray-600 leading-relaxed mb-6 line-clamp-3">
          {item.content}
        </p> */}
        <div className="mb-4">
          <p className="text-[15px] sm:text-[16px] text-gray-500 leading-[1.8]">
            This post contains inappropriate language that violates community guidelines...
          </p>
        </div>

        {/* Reason */}
        <div className="mb-5">
          <p className="text-[14px] sm:text-[15px] text-gray-700">
            Reason: <span className="font-bold text-gray-900">Hate speech</span>
          </p>
        </div>


        <div className="grid grid-cols-2 sm:flex gap-3">
          <ActionButton icon={Preview} label="Preview" onClick={() => onPreview(item)} />
          <ActionButton icon={Remove} label="Remove" danger onClick={() => onRemove(item)} />
          <ActionButton icon={Suspend} label="Suspend User" danger onClick={() => onSuspend(item)} />
          <ActionButtonv2
            onClick={() => onApprove(item)}
            label="Approve"
            icon={<CheckCheck />}
          />
        </div>
      </div>
    </div>
  );
}
function ContentCard({ item, onPreview, type, onRemove, onSuspend, onUnsuspend, onApprove }) {
  return (
    <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden mb-5">
      <div className="px-5 sm:px-6 py-6">

        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <Avatar name={item?.name} color={item.avatarColor} avatar={item?.avatar} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-semibold text-gray-900 text-[18px] sm:text-[21px] truncate">
                {item?.name}
              </span>
              <span className="text-[11px] sm:text-[12px] px-3 py-1 rounded-full border border-gray-300 text-gray-600 font-medium whitespace-nowrap">
                {type}
              </span>
            </div>

            <p className="text-[13px] sm:text-[14px] text-gray-400">{getTimeAgo(item.created_at)}</p>
          </div>

          {/* === NEW STATUS BADGE === */}
          <PostStatusBadge status={item.moderation_status} />
        </div>

        {/* Content */}
        {item.moderation_status === "pending" && (
          <div className="mb-4">
            <p className="text-[15px] sm:text-[16px] text-gray-500 leading-[1.8]">
              This post contains inappropriate language that violates community guidelines...
            </p>
          </div>
        )}

        {/* Reason */}
        {(item.moderation_reason && item.moderation_status !== 'approved' && item.moderation_queue_id) && (
          <div className="mb-5">
            <p className="text-[14px] sm:text-[15px] text-gray-700">
              Reason: <span className="font-bold text-gray-900">{item.moderation_reason}</span>
            </p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <ActionButton icon={Preview} label="Preview" onClick={() => onPreview(item)} />

          {item.moderation_status === 'pending' && (
            <ActionButton icon={Remove} label="Remove" danger onClick={() => onRemove(item)} />
          )}

          {item.moderation_status && item.moderation_status !== 'approved' && (
            <>
              {item.moderation_status === 'suspended' ? (
                <ActionButtonv2 icon={<Undo />} label="Unsuspend User" onClick={() => onUnsuspend(item)} />
              ) : (
                <ActionButton icon={Suspend} label="Suspend User" danger onClick={() => onSuspend(item)} />
              )}
              <ActionButtonv2 icon={<CheckCheckIcon />} label="Approve" onClick={() => onApprove(item)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ContentManagement() {

  const queryClient = useQueryClient();
  const [tab, setTab] = useState('flagged');
  const tabRefs = useRef({});
  const [pillStyle, setPillStyle] = useState({ width: 0, left: 0 });

  // 2. Measure whenever tab changes
  useEffect(() => {
    const el = tabRefs.current[tab];
    if (el) setPillStyle({ width: el.offsetWidth, left: el.offsetLeft });
  }, [tab]);
  const [page, setPage] = useState(1);
  const [previewItem, setPreviewItem] = useState(null);

  const [removeModal, setRemoveModal] = useState({ isOpen: false, item: null });
  const [suspendModal, setSuspendModal] = useState({ isOpen: false, item: null });
  const [unsuspendModal, setUnsuspendModal] = useState({ isOpen: false, item: null });

  const [approveModal, setApproveModal] = useState({ isOpen: false, item: null });

  const limit = 10;

  // Dynamic query based on active tab
  const { data, isLoading } = useQuery({
    queryKey: [tab, page],
    queryFn: () => {
      if (tab === 'flagged') {
        return api.getModerationItemList(page);
      } else if (tab === 'all_posts') {
        return api.getAllPosts(page)
      } else if (tab === 'all_comments') {
        return api.getAllComments(page)
      }
      return Promise.resolve({ data: [], pagination: { page: 1, totalPages: 1 } });
    },
    enabled: true,
  });

  const items = data?.data || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNextPage: false, hasPrevPage: false };

  const removeMutation = useMutation({
    mutationFn: (id) => api.removeModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tab] });
      setRemoveModal({ isOpen: false, item: null });
    },
  });

  const suspendMutation = useMutation({
    mutationFn: (id) => api.suspendUserModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tab] });
      setSuspendModal({ isOpen: false, item: null });
    },
  });
  const unsuspendMutation = useMutation({
    mutationFn: (id) => api.unsuspendUserModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tab] });
      setUnsuspendModal({ isOpen: false, item: null });
    },
  });


  const approveMutation = useMutation({
    mutationFn: (id) => api.approveModerationItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tab] });
      setApproveModal({ isOpen: false, item: null });
    },
  });

  const handleRemove = (item) => setRemoveModal({ isOpen: true, item });
  const handleSuspend = (item) => setSuspendModal({ isOpen: true, item });
  const handleUnsuspend = (item) => setUnsuspendModal({ isOpen: true, item });

  const handleApprove = (item) => setApproveModal({ isOpen: true, item });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
    }
  };

  return (
    <div className="w-full bg-gray-50 min-h-screen pb-12" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-500 mt-1">Review and moderate community content</p>
      </div>

      {/* Tabs */}
      <div className="relative w-full max-w-md h-16 bg-[#F0F0F4] rounded-3xl p-1.5 flex mb-8">
        {['flagged', 'all_posts', 'all_comments'].map((t) => (
          <button
            key={t}
            ref={(el) => { tabRefs.current[t] = el; }}
            onClick={() => { setTab(t); setPage(1); }}
            className={`relative z-10 flex-1 h-14 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${tab === t ? 'text-black' : 'text-gray-500'
              }`}
          >
            {t === 'flagged'
              ? <AlertTriangle size={20} />
              : <SlidersHorizontal size={20} />}

            <span>
              {t === 'flagged'
                ? 'Flagged'
                : t === 'all_posts'
                  ? 'All Posts'
                  : 'All Comments'}
            </span>
          </button>
        ))}

        <div
          className="absolute top-1.5 h-14 bg-white rounded-2xl shadow transition-all duration-500"
          style={{
            width: pillStyle.width,
            transform: `translateX(${pillStyle.left}px)`,
          }}
        />
      </div>

      {/* Content List */}
      <div>
        {isLoading ? (
          <p className="text-center py-12 text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="text-center py-12 text-gray-500">No content found.</p>
        ) : (
          items.map((item) => (
            tab === 'flagged' ? (
              <PostCard
                key={item.id}
                item={item}
                onPreview={setPreviewItem}
                onRemove={handleRemove}
                onSuspend={handleSuspend}
                onApprove={handleApprove}
              />
            ) : (
              <ContentCard
                key={item.id}
                item={item}
                type={tab == "all_posts" ? 'Post' : 'Comment'}
                onPreview={setPreviewItem}
                onRemove={handleRemove}
                onSuspend={handleSuspend}
                onUnsuspend={handleUnsuspend}
                onApprove={handleApprove}
              />
            )
          ))
        )}
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={handlePageChange} />

      {/* Preview Modal */}
      {previewItem && <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />}

      {/* Action Modals */}
      <ActionModal
        isOpen={removeModal.isOpen}
        onRequestClose={() => setRemoveModal({ isOpen: false, item: null })}
        onConfirm={() => removeMutation.mutate(removeModal.item?.moderation_queue_id || removeModal.item?.id)}
        actionType="remove"
        isLoading={removeMutation.isPending}
      />

      <ActionModal
        isOpen={suspendModal.isOpen}
        onRequestClose={() => setSuspendModal({ isOpen: false, item: null })}
        onConfirm={() => suspendMutation.mutate(suspendModal.item?.moderation_queue_id || suspendModal.item?.id)}
        actionType="suspend"
        isLoading={suspendMutation.isPending}
      />
      <ActionModal
        isOpen={unsuspendModal.isOpen}
        onRequestClose={() => setUnsuspendModal({ isOpen: false, item: null })}
        onConfirm={() => unsuspendMutation.mutate(unsuspendModal.item?.moderation_queue_id || unsuspendModal.item?.id)}
        actionType="unsuspend"
        isLoading={unsuspendMutation.isPending}
      />

      <ActionModal
        isOpen={approveModal.isOpen}
        onRequestClose={() => setApproveModal({ isOpen: false, item: null })}
        onConfirm={() => approveMutation.mutate(approveModal.item?.moderation_queue_id || approveModal.item?.id)}
        actionType="approve"
        isLoading={approveMutation.isPending}
      />
    </div>
  );
}